import { destroyVenueMap } from "./venueMapKit";
import type { AppLocale } from "./i18n/types";
import type { LandingStrings } from "./i18n/landingTypes";
import { LEGAL_PRIVACY_HTML } from "./i18n/legalPrivacyEn";
import { LEGAL_TERMS_HTML } from "./i18n/legalTermsEn";
import { ALL_APP_LOCALES, LOCALE_LABELS, LOCALE_SELECT_LABELS, persistAppLocale } from "./i18n/resolveLocale";

const APP_STORE_FALLBACK = "https://apps.apple.com/app/wedy/id000000000";

function escapeHtml(s: string): string {
  return s
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

function el(html: string): HTMLElement {
  const t = document.createElement("template");
  t.innerHTML = html.trim();
  const n = t.content.firstElementChild;
  if (!(n instanceof HTMLElement)) throw new Error("template");
  return n;
}

function getAppContentMount(root: HTMLElement): HTMLElement {
  const inner = root.querySelector(".wedy-app-inner");
  return inner instanceof HTMLElement ? inner : root;
}

function getVideoBase(): string {
  const raw = import.meta.env.VITE_VIDEO_ASSET_BASE_URL?.trim() ?? "";
  return raw.replace(/\/$/, "");
}

function sceneVideoSrc(scene: 1 | 2 | 3 | 4 | 5 | 6): string {
  const base = getVideoBase();
  const path = `/onboarding/OnboardingScene${scene}.mp4`;
  return base ? `${base}${path}` : path;
}

function appStoreHref(): string {
  const a = document.querySelector<HTMLAnchorElement>(".wedy-app-promo__link");
  const h = a?.href?.trim();
  return h && h.length > 0 ? h : APP_STORE_FALLBACK;
}

/** Yalnızca bir `<details>` açık kalsın (accordion). */
function attachExclusiveLandingFaq(scope: HTMLElement): void {
  const faqRoot = scope.querySelector("[data-wedy-faq]");
  if (!faqRoot) return;
  const items = faqRoot.querySelectorAll<HTMLDetailsElement>(".wedy-faq__item");
  items.forEach((detail) => {
    detail.addEventListener("toggle", () => {
      if (!detail.open) return;
      items.forEach((other) => {
        if (other !== detail) other.open = false;
      });
    });
  });
}

function attachLazyVideos(root: HTMLElement): void {
  const nodes = root.querySelectorAll<HTMLElement>("[data-wedy-video-src]");
  if (nodes.length === 0) return;

  const load = (video: HTMLVideoElement): void => {
    const src = video.dataset.wedyVideoSrc;
    if (!src) return;
    video.src = src;
    video.load();
    void video.play().catch(() => {
      /* autoplay policy — muted loop usually ok */
    });
  };

  if (!("IntersectionObserver" in window)) {
    nodes.forEach((node) => {
      if (node instanceof HTMLVideoElement) load(node);
    });
    return;
  }

  const io = new IntersectionObserver(
    (entries) => {
      for (const e of entries) {
        if (!e.isIntersecting) continue;
        const v = e.target;
        if (v instanceof HTMLVideoElement) {
          load(v);
          io.unobserve(v);
        }
      }
    },
    { root: null, rootMargin: "120px 0px", threshold: 0.01 },
  );

  nodes.forEach((node) => {
    if (node instanceof HTMLVideoElement) io.observe(node);
  });
}

function languageSelectHtml(current: AppLocale): string {
  const options = ALL_APP_LOCALES.map((loc) => {
    const compact = LOCALE_SELECT_LABELS[loc];
    const fullName = LOCALE_LABELS[loc];
    const sel = loc === current ? " selected" : "";
    return `<option value="${escapeHtml(loc)}" title="${escapeHtml(fullName)}"${sel}>${escapeHtml(compact)}</option>`;
  }).join("");
  return `<div class="wedy-landing__lang-row">
    <select class="wedy-landing__lang-select" data-wedy-lang-select>${options}</select>
  </div>`;
}

function attachLanguageSelect(root: HTMLElement, landing: LandingStrings): void {
  const select = root.querySelector<HTMLSelectElement>("[data-wedy-lang-select]");
  if (!select) return;

  select.setAttribute("aria-label", landing.footerLanguage);

  select.addEventListener("change", () => {
    const v = select.value as AppLocale;
    persistAppLocale(v);
    const url = new URL(window.location.href);
    url.searchParams.set("lang", v);
    window.location.assign(url.toString());
  });
}

/** Mevcut `?lang=` ile aynı kalır (landing ve yasal sayfalar). */
function pathWithCurrentLang(path: string): string {
  const u = new URL(path || "/", window.location.origin);
  const cur = new URL(window.location.href);
  const lang = cur.searchParams.get("lang");
  if (lang) u.searchParams.set("lang", lang);
  return u.pathname + (u.search ? u.search : "");
}

function landingTopbarInnerHtml(
  locale: AppLocale,
  landing: LandingStrings,
  active: "home" | "privacy" | "terms",
): string {
  const navLabel = `${landing.navHome} · ${landing.footerPrivacy} · ${landing.footerTerms}`;
  const homeHref = escapeHtml(pathWithCurrentLang("/"));
  const privacyHref = escapeHtml(pathWithCurrentLang("/privacy"));
  const termsHref = escapeHtml(pathWithCurrentLang("/terms"));
  const homeCurrent = active === "home" ? ` aria-current="page"` : "";
  const privacyCurrent = active === "privacy" ? ` aria-current="page"` : "";
  const termsCurrent = active === "terms" ? ` aria-current="page"` : "";
  return `<div class="wedy-landing__topbar-inner">
      <nav class="wedy-landing__legal" aria-label="${escapeHtml(navLabel)}">
        <a class="wedy-landing__legal-link" href="${homeHref}"${homeCurrent}>${escapeHtml(landing.navHome)}</a>
        <span class="wedy-landing__dot" aria-hidden="true">·</span>
        <a class="wedy-landing__legal-link" href="${privacyHref}"${privacyCurrent}>${escapeHtml(landing.footerPrivacy)}</a>
        <span class="wedy-landing__dot" aria-hidden="true">·</span>
        <a class="wedy-landing__legal-link" href="${termsHref}"${termsCurrent}>${escapeHtml(landing.footerTerms)}</a>
      </nav>
      ${languageSelectHtml(locale)}
    </div>`;
}

/**
 * Kök / geçersiz davet: üst çubuk (dil + yasal) + kahraman + 6 bölüm (video) + altbilgi.
 * Davet akışındaki promo footer gizlenir (`.wedy-root--landing`).
 */
export function renderLanding(root: HTMLElement, locale: AppLocale, landing: LandingStrings): void {
  destroyVenueMap();
  root.classList.remove("wedy-root--hero-bg");
  root.style.removeProperty("--wedy-hero-bg");
  root.classList.add("wedy-root--landing");

  const year = String(new Date().getFullYear());
  const copyright = landing.footerCopyright.replace("{year}", year);
  const store = appStoreHref();

  const badgeSrc =
    "https://tools.applemediaservices.com/api/badges/download-on-the-app-store/black/en-us?size=250x83&releaseDate=1276560000";

  const sections = landing.slides
    .map((slide, i) => {
      const n = (i + 1) as 1 | 2 | 3 | 4 | 5 | 6;
      const vidSrc = escapeHtml(sceneVideoSrc(n));
      return `<section class="wedy-landing__section wedy-card wedy-card--thin" aria-labelledby="wedy-slide-${i}-title">
        <div class="wedy-landing__section-text">
          <h2 id="wedy-slide-${i}-title" class="wedy-landing__section-title">${escapeHtml(slide.title)}</h2>
          <p class="wedy-landing__section-body wedy-subtext">${escapeHtml(slide.body)}</p>
        </div>
        <div class="wedy-landing__video-wrap">
          <video
            class="wedy-landing__video"
            muted
            playsinline
            loop
            preload="none"
            data-wedy-video-src="${vidSrc}"
            aria-label="${escapeHtml(slide.videoAria)}"
          ></video>
        </div>
      </section>`;
    })
    .join("");

  const faqItems = landing.faqItems
    .map((item, i) => {
      const sid = `wedy-faq-${i}`;
      return `<details class="wedy-faq__item">
        <summary id="${sid}-summary" class="wedy-faq__summary">${escapeHtml(item.q)}</summary>
        <div class="wedy-faq__panel">
          <p id="${sid}-body" class="wedy-faq__answer wedy-subtext">${escapeHtml(item.a)}</p>
        </div>
      </details>`;
    })
    .join("");

  const main = el(`<main class="wedy-landing">
    <div class="wedy-landing__topbar" role="banner">
      ${landingTopbarInnerHtml(locale, landing, "home")}
    </div>
    <header class="wedy-landing__hero wedy-card wedy-card--thin">
      <h1 class="wedy-landing__brand playfair-display-names">${escapeHtml(landing.heroTitle)}</h1>
      <p class="wedy-landing__tagline wedy-subtext">${escapeHtml(landing.heroSubtitle)}</p>
      <a
        class="wedy-landing__store-link"
        href="${escapeHtml(store)}"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="${escapeHtml(landing.ctaAppStoreAria)}"
      >
        <img
          class="wedy-landing__store-badge"
          src="${badgeSrc}"
          alt=""
          width="156"
          height="52"
          loading="eager"
          decoding="async"
        />
      </a>
    </header>
    <div class="wedy-landing__sections" role="region" aria-label="${escapeHtml(landing.pageMetaTitle)}">
      ${sections}
    </div>
    <section
      class="wedy-faq wedy-card wedy-card--thin"
      aria-labelledby="wedy-faq-title"
      data-wedy-faq
    >
      <h2 id="wedy-faq-title" class="wedy-faq__title">${escapeHtml(landing.faqTitle)}</h2>
      <div class="wedy-faq__list">${faqItems}</div>
    </section>
    <footer class="wedy-landing__footer">
      <p class="wedy-landing__copy wedy-subtext">${escapeHtml(copyright)}</p>
    </footer>
  </main>`);

  getAppContentMount(root).replaceChildren(main);
  attachLazyVideos(main);
  attachExclusiveLandingFaq(main);
  attachLanguageSelect(root, landing);
}

/**
 * Gizlilik / şartlar — landing ile aynı üst çubuk + cam kart içerik.
 */
export function renderLegalPage(
  root: HTMLElement,
  locale: AppLocale,
  landing: LandingStrings,
  kind: "privacy" | "terms",
): void {
  destroyVenueMap();
  root.classList.remove("wedy-root--hero-bg");
  root.style.removeProperty("--wedy-hero-bg");
  root.classList.add("wedy-root--landing");

  const year = String(new Date().getFullYear());
  const copyright = landing.footerCopyright.replace("{year}", year);
  const title = kind === "privacy" ? landing.footerPrivacy : landing.footerTerms;
  const richHtml = kind === "privacy" ? LEGAL_PRIVACY_HTML : LEGAL_TERMS_HTML;
  const bodyMarkup = `<div class="wedy-legal__body wedy-subtext wedy-legal__body--rich">${richHtml}</div>`;

  const main = el(`<main class="wedy-landing">
    <div class="wedy-landing__topbar" role="banner">
      ${landingTopbarInnerHtml(locale, landing, kind)}
    </div>
    <article class="wedy-legal wedy-card wedy-card--thin">
      <h1 class="wedy-legal__title playfair-display-names">${escapeHtml(title)}</h1>
      ${bodyMarkup}
    </article>
    <footer class="wedy-landing__footer">
      <p class="wedy-landing__copy wedy-subtext">${escapeHtml(copyright)}</p>
    </footer>
  </main>`);

  getAppContentMount(root).replaceChildren(main);
  attachLanguageSelect(root, landing);
}
